import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, unwrapApiResponse } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import MobileBottomNav from '@/components/layout/MobileBottomNav';
import { Heart, Trash2, User, ChevronLeft, Edit2, List, X, Loader2, Globe, Lock } from 'lucide-react';
import MinimalistWorkCard from '@/components/obra/MinimalistWorkCard';
import { Input } from '@/components/ui/Input';

type ListaUsuario = { id?: number; nick?: string; nome?: string };
type ListaObra = { id: number; nome?: string; imagem?: string | null };

type ListaDetalhe = {
    id: number;
    nome: string;
    descricao?: string | null;
    publica: boolean;
    total_obras?: number;
    usuario?: ListaUsuario;
    obras?: ListaObra[];
    seguindo?: boolean;
    isOwner?: boolean;
};

export default function ListaDetalhePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const [lista, setLista] = useState<ListaDetalhe | null>(null);
    const [loading, setLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editFormData, setEditFormData] = useState({ nome: '', descricao: '', publica: false });

    const fetchListaDetails = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await api.get(`listas/${id}`);
            const data = unwrapApiResponse<Record<string, unknown>>(res);
            if (data && typeof data === 'object' && typeof data.id === 'number') {
                const ownerId = (data.usuario as ListaUsuario | undefined)?.id;
                const isOwner = Boolean(isAuthenticated && ownerId != null && ownerId === user?.usr_id);
                setLista({
                    ...(data as unknown as ListaDetalhe),
                    isOwner,
                });
            } else {
                setLista(null);
            }
        } catch {
            navigate('/listas');
        } finally {
            setLoading(false);
        }
    }, [id, isAuthenticated, user?.usr_id, navigate]);

    useEffect(() => {
        void fetchListaDetails();
    }, [fetchListaDetails]);

    const handleUpdateLista = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editFormData.nome.trim() || !id) return;
        setIsSubmitting(true);
        try {
            await api.patch(`listas/${id}`, editFormData);
            setIsEditModalOpen(false);
            await fetchListaDetails();
        } catch {
            alert('Erro ao atualizar lista.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFollow = async () => {
        if (!isAuthenticated || !lista) return;
        try {
            await api.post(`listas/${lista.id}/seguir`, {});
            await fetchListaDetails();
        } catch (error) {
            console.error(error);
        }
    };

    const handleRemoveObra = async (e: React.MouseEvent, obraId: number) => {
        e.preventDefault();
        e.stopPropagation();
        if (!lista?.isOwner || !window.confirm('Remover obra desta lista?')) return;
        try {
            await api.post(`listas/${lista.id}/adicionar-remover-obra`, { obra: obraId });
            await fetchListaDetails();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeleteLista = async () => {
        if (!lista?.isOwner || !window.confirm('Excluir esta lista?')) return;
        try {
            await api.delete(`listas/${lista.id}`);
            navigate('/colecao?aba=listas');
        } catch (error) {
            console.error(error);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-black">
                <Loader2 className="size-10 animate-spin text-orange-500" />
            </div>
        );
    }
    if (!lista) return null;

    const obras = lista.obras ?? [];
    const totalObras = typeof lista.total_obras === 'number' ? lista.total_obras : obras.length;

    return (
        <div className="min-h-screen bg-black pb-24 text-white md:pb-10">
            <Navbar />
            <main className="mx-auto max-w-7xl px-4 pt-28 lg:pt-36">
                <div className="mb-8 flex flex-wrap items-center gap-3">
                    <Link
                        to="/listas"
                        className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm font-semibold text-white/60 transition-colors hover:bg-white/5 hover:text-white"
                    >
                        <ChevronLeft size={18} className="shrink-0" />
                        Listas públicas
                    </Link>
                    {isAuthenticated && (
                        <Link
                            to="/colecao?aba=listas"
                            className="text-sm font-medium text-white/35 underline-offset-4 hover:text-white/60 hover:underline"
                        >
                            Minha coleção
                        </Link>
                    )}
                </div>

                <div className="mb-8 rounded-2xl bg-neutral-950/80 p-5">
                    <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
                        <div className="min-w-0 flex-1 space-y-4">
                            <div className="flex items-start gap-4">
                                <div className="min-w-0">
                                    <div className="mb-2 flex flex-wrap items-center gap-2">
                                        <span
                                            className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${lista.publica ? 'border-emerald-500/25 text-emerald-400/90' : 'border-white/10 text-white/45'}`}
                                        >
                                            {lista.publica ? <Globe className="size-3" /> : <Lock className="size-3" />}
                                            {lista.publica ? 'Pública' : 'Privada'}
                                        </span>
                                        <span className="text-[11px] font-medium text-white/40">
                                            {totalObras} {totalObras === 1 ? 'obra' : 'obras'}
                                        </span>
                                    </div>
                                    <h1 className="text-2xl font-bold tracking-tight text-white md:text-4xl md:leading-tight">{lista.nome}</h1>
                                </div>
                            </div>
                            <p className="max-w-3xl text-base leading-relaxed text-white/50 md:text-lg">
                                {lista.descricao?.trim() || 'Sem descrição.'}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 pt-1 text-sm text-white/45">
                                <div className="flex size-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.04]">
                                    <User size={14} className="text-white/50" />
                                </div>
                                <span className="font-medium">
                                    Por{' '}
                                    <span className="text-white/80">{lista.usuario?.nick ?? lista.usuario?.nome ?? '—'}</span>
                                </span>
                            </div>
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-2">
                            {!lista.isOwner && lista.publica && (
                                <button
                                    type="button"
                                    onClick={handleFollow}
                                    className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold transition-colors ${
                                        lista.seguindo
                                            ? 'border border-white/10 bg-white/[0.06] text-white/60 hover:bg-white/10'
                                            : 'bg-orange-600 text-white hover:bg-orange-500'
                                    }`}
                                >
                                    <Heart size={18} className={lista.seguindo ? 'fill-current' : ''} />
                                    {lista.seguindo ? 'Seguindo' : 'Seguir'}
                                </button>
                            )}
                            {lista.isOwner && (
                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditFormData({
                                                nome: lista.nome,
                                                descricao: lista.descricao || '',
                                                publica: lista.publica,
                                            });
                                            setIsEditModalOpen(true);
                                        }}
                                        className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/[0.08]"
                                    >
                                        <Edit2 size={18} className="text-orange-500" />
                                        Editar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDeleteLista}
                                        className="rounded-xl border border-white/10 bg-white/[0.04] p-3.5 text-white/50 transition-colors hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-400"
                                        aria-label="Excluir lista"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {obras.length === 0 ? (
                    <p className="py-12 text-center text-sm text-white/40">Esta lista ainda não tem obras.</p>
                ) : (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 sm:gap-4">
                        {obras.map((item) => (
                            <MinimalistWorkCard
                                key={item.id}
                                obra={{
                                    id: item.id,
                                    nome: item.nome ?? 'Sem título',
                                    imagem: item.imagem ?? undefined,
                                }}
                                viewMode="grid"
                                showFavoriteButton={false}
                                topRightAction={
                                    lista.isOwner ? (
                                        <button
                                            type="button"
                                            onClick={(e) => handleRemoveObra(e, item.id)}
                                            className="absolute right-2 top-2 z-20 rounded-lg border border-white/10 bg-black/60 p-2 text-white/70 opacity-0 backdrop-blur-sm transition-all hover:bg-red-500/80 hover:text-white group-hover:opacity-100"
                                            aria-label="Remover da lista"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    ) : undefined
                                }
                            />
                        ))}
                    </div>
                )}
            </main>

            {isEditModalOpen && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center px-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => !isSubmitting && setIsEditModalOpen(false)} />
                    <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-neutral-950 shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="flex items-center justify-between border-b border-white/5 px-5 py-4">
                            <h3 className="text-lg font-bold text-white">Editar lista</h3>
                            <button
                                type="button"
                                disabled={isSubmitting}
                                onClick={() => setIsEditModalOpen(false)}
                                className="text-white/40 transition-colors hover:text-white disabled:opacity-40"
                                aria-label="Fechar"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateLista} className="space-y-4 p-5">
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-white/50">Nome</label>
                                <Input
                                    type="text"
                                    required
                                    value={editFormData.nome}
                                    onChange={(e) => setEditFormData({ ...editFormData, nome: e.target.value })}
                                    className="rounded-xl py-3"
                                />
                            </div>
                            <div>
                                <label className="mb-1.5 block text-xs font-medium text-white/50">Descrição</label>
                                <textarea
                                    value={editFormData.descricao}
                                    onChange={(e) => setEditFormData({ ...editFormData, descricao: e.target.value })}
                                    rows={3}
                                    className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-orange-500/50"
                                />
                            </div>
                            <div>
                                <p className="mb-2 text-xs font-medium text-white/50">Visibilidade</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setEditFormData({ ...editFormData, publica: false })}
                                        className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${!editFormData.publica ? 'border-orange-500/40 bg-orange-500/10 text-orange-400' : 'border-white/10 text-white/45 hover:bg-white/5'}`}
                                    >
                                        Privada
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditFormData({ ...editFormData, publica: true })}
                                        className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${editFormData.publica ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400' : 'border-white/10 text-white/45 hover:bg-white/5'}`}
                                    >
                                        Pública
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full rounded-xl bg-orange-600 py-3.5 text-sm font-bold text-white transition-colors hover:bg-orange-500 disabled:opacity-50"
                            >
                                {isSubmitting ? 'Salvando…' : 'Salvar'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
            <MobileBottomNav />
        </div>
    );
}
